/* How soon a party can be seated side by side for the same service.
 *
 * Two friends after gel pedicures want to sit together, which is two chairs
 * starting at about the same moment. Next availability answers for one person:
 * the soonest anybody has room. For two that is the wrong question — the first
 * pedicurist free now and the second not for forty minutes is a forty-minute
 * wait for the pair, or a party split across the afternoon. Kevin, 2026-10-25:
 * the desk picks a party size beside Next availability, from one to five, and
 * every row answers for that many people starting within five minutes of each
 * other — when, who, and what has to move in the book to make it so.
 *
 * Within five minutes, not at the same minute: a technician free a little
 * early waits for the one finishing up, and two clients shown to their chairs a
 * few minutes apart are still sitting together. So the answer is the earliest
 * moment S at which `size` different technicians can each start somewhere in
 * [S, S + window].
 *
 * `startFrom(id, S)` is the board's own measurement — the soonest this
 * technician could start the work if asked from S, or null for no answer today
 * — so a party of one reads exactly what the row already said. It never goes
 * backwards: asked later, the answer is never earlier. That is what makes the
 * search a short walk rather than a scan of the afternoon minute by minute.
 * When S fails, every technician's answer from S is a floor for any later S,
 * so nothing before (size-th soonest − window) can succeed either, and the
 * search jumps straight there.
 *
 * Pure; this file imports nothing.
 */

export var PARTY_MIN = 1;
export var PARTY_MAX = 5;
export var PARTY_WINDOW_MIN = 5;

// A party size from whatever the field held: a whole number, one to five.
export function cleanPartySize(n) {
  var v = Math.round(Number(n));
  if (!isFinite(v)) return PARTY_MIN;
  return Math.max(PARTY_MIN, Math.min(PARTY_MAX, v));
}

function byStart(a, b) {
  return (a.at - b.at) || (rankOf(a) - rankOf(b));
}

function rankOf(c) { return c.rank == null ? Infinity : c.rank; }

/* The soonest the party can sit down together, or null when today has no such
 * moment.
 *
 *   techs      [{ id, rank }] — everybody who does the service. `rank` their
 *              place in the rotation, the tie-break when two could start at the
 *              same minute; null for somebody not on it.
 *   size       how many people.
 *   from       now, in ms.
 *   windowMs   how far apart the starts may be.
 *   startFrom  (id, ms) → ms | null.
 *
 * Returns { from, by, seats: [{ id, rank, at }] }: the first and last start,
 * and who sits where, soonest first. Anybody ready before the last one in is
 * moved to start with them when they still have room then, so a pair reads as
 * one start time unless the book really does keep them a few minutes apart. */
export function soonestTogether(techs, size, from, windowMs, startFrom) {
  var list = techs || [];
  var n = Math.max(1, size | 0);
  if (list.length < n) return null;
  var S = from;
  // Each pass either answers or moves S strictly later, and every technician's
  // answer only ever grows — but a bound costs nothing against a bad measure.
  for (var guard = 0; guard < 500; guard++) {
    var cands = [];
    list.forEach(function (t) {
      var at = startFrom(t.id, S);
      if (at != null) cands.push({ id: t.id, rank: t.rank == null ? null : t.rank, at: at });
    });
    if (cands.length < n) return null;
    cands.sort(byStart);
    var kth = cands[n - 1].at;
    if (kth <= S + windowMs) {
      var seats = cands.filter(function (c) { return c.at <= S + windowMs; })
        .slice(0, n);
      // Side by side where it can be: anybody ready early starts with the last
      // one in, if they still have room then. The window is slack for the ones
      // who do not, not a reason to seat people apart.
      var by = seats[n - 1].at;
      seats.forEach(function (c) {
        if (c.at < by && startFrom(c.id, by) === by) c.at = by;
      });
      seats.sort(byStart);
      return { from: seats[0].at, by: by, seats: seats };
    }
    S = kth - windowMs;
  }
  return null;
}

/* The bookings the party's own technicians are being counted on to cover.
 *
 * What makes a technician free for a walk-in is sometimes a booking of theirs
 * written off to a colleague. For one person that is a handover; for a party it
 * can be a circle — Ann is free because her 3:00 goes to Bea, and Bea is in
 * the next chair doing the other pedicure. That plan does not hold, and the
 * board's ledger cannot see it, because it was drawn up for one walk-in.
 *
 *   seats   [{ id, at, moves: [{ to: { id }, startsAt, endsAt, ... }] }]
 *   needMs  how long the party's service runs.
 *
 * Returns every move whose stand-in is seated with the party at the time the
 * move would need them, as { seat, move }. */
export function partyClashes(seats, needMs) {
  var busy = {};
  (seats || []).forEach(function (s) { busy[s.id] = { from: s.at, to: s.at + needMs }; });
  var out = [];
  (seats || []).forEach(function (s) {
    (s.moves || []).forEach(function (m) {
      var b = m.to && busy[m.to.id];
      if (b && m.startsAt < b.to && m.endsAt > b.from) out.push({ seat: s, move: m });
    });
  });
  return out;
}
